import * as React from 'react';

import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import {
  enumFreightTerm,
  enumPaymentTerm,
  enumQuoteType,
  FreightTerm,
  PaymentTerm,
  QuoteType,
} from '@rematter/frontend-lib-api-client';
import {
  Checkbox,
  ContentSection,
  Toggle,
  ToggleButtonGroup,
} from '@rematter/ui-web';
import { SmartDatePicker } from '@rematter/ui-web/components';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { LucideArrowLeftToLine, LucideArrowRightToLine } from 'lucide-react';

import { $quoteForm, $updateQuoteForm } from '../../_state/quoteForm.state';
import { useTranslations } from '../../i18n/useTranslations';
import { QuoteFormState } from '../../types/quote';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

type Props = {
  mode: 'create' | 'edit';
  disabled?: boolean;
  className?: string;
};

const quoteFormSelector = (state: QuoteFormState) => ({
  type: state.type,
  accountId: state.accountId,
  facilityId: state.facilityId,
  validUntil: state.validUntil,
  expectedShipDate: state.expectedShipDate,
  paymentTerm: state.paymentTerm,
  paymentCurrency: state.paymentCurrency,
  freightTerm: state.freightTerm,
  freightCoveredByCustomer: state.freightCoveredByCustomer,
});

const $quoteInfoForm = selectAtom($quoteForm, quoteFormSelector, isEqual);

//
// ⚛️ ⚛️ ⚛️ QUOTE INFO FORM ⚛️ ⚛️ ⚛️
//
export const QuoteInfoForm = ({ mode, disabled = false, className }: Props) => {
  const t = useTranslations();

  // Atoms
  const form = useAtomValue($quoteInfoForm);
  const updateForm = useSetAtom($updateQuoteForm);

  // Callbacks
  const handleFieldChange = React.useCallback(
    <K extends keyof QuoteFormState>(field: K) =>
      (value: QuoteFormState[K]) => {
        updateForm({ [field]: value });
      },
    [updateForm],
  );

  return (
    <div className={cn('@QuoteInfoForm', className)}>
      {mode === 'create' && (
        <ToggleButtonGroup
          className="p-5 *:w-full"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[form.type ?? enumQuoteType.Sales]}
          onSelectionChange={(keys) => {
            const value = Array.from(keys)[0] as QuoteType;
            handleFieldChange('type')(value);
          }}
        >
          <Toggle id={enumQuoteType.Sales}>
            <span className="flex w-full items-center gap-3">
              <LucideArrowLeftToLine className="size-4" />
              {t.quotes.types.selling}
            </span>
          </Toggle>
          <Toggle id={enumQuoteType.Purchase}>
            <span className="flex w-full items-center gap-3">
              <LucideArrowRightToLine className="size-4" />
              {t.quotes.types.buying}
            </span>
          </Toggle>
        </ToggleButtonGroup>
      )}
      <ContentSection
        title={mode === 'edit' && t.quotes.detail.sections.quoteInfo}
        variant="panel"
        hideDivider={mode === 'create'}
      >
        <div
          className={cn(
            'grid gap-4',
            mode === 'create' && 'grid-cols-1',
            mode === 'edit' && 'grid-cols-3',
          )}
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectFacility
              label={t.quotes.detail.fields.facility}
              value={form.facilityId}
              onChange={handleFieldChange('facilityId')}
              autoSelectFirstOption
              isRequired
              isDisabled={disabled}
            />

            <SelectAccount
              label={
                mode === 'create'
                  ? t.quotes.create.fields.account
                  : t.quotes.edit.fields.account
              }
              value={form.accountId}
              onChange={handleFieldChange('accountId')}
              isRequired
              placeholder={
                mode === 'create'
                  ? t.common.placeholders.searchAccount
                  : t.quotes.edit.placeholders.selectAccount
              }
              isDisabled={disabled}
            />
          </div>
          <div
            className={cn(
              'grid grid-cols-2 gap-4',
              mode === 'edit' && 'col-span-2',
            )}
          >
            <SmartDatePicker
              label={t.quotes.create.fields.validUntil}
              value={maybeParseCalendarDate(form.validUntil)}
              onChange={(date) =>
                handleFieldChange('validUntil')(date?.toString() ?? null)
              }
              isRequired
              isDisabled={disabled}
            />

            <SmartDatePicker
              label={t.quotes.edit.fields.shipDate}
              value={maybeParseCalendarDate(form.expectedShipDate)}
              onChange={(date) =>
                handleFieldChange('expectedShipDate')(date?.toString() ?? null)
              }
              isRequired
              isDisabled={disabled}
            />
          </div>
        </div>
      </ContentSection>

      <ContentSection
        title={mode === 'edit' && t.quotes.detail.sections.paymentInfo}
        variant="panel"
        hideDivider
      >
        <div className="grid grid-cols-2 gap-4">
          <SelectEnum<PaymentTerm>
            enumValue={enumPaymentTerm}
            value={form.paymentTerm}
            onChange={handleFieldChange('paymentTerm')}
            label={t.quotes.detail.fields.paymentTerm}
            isDisabled={disabled}
          />
          <SelectCurrency
            value={form.paymentCurrency}
            onChange={handleFieldChange('paymentCurrency')}
            label={t.quotes.detail.fields.paymentCurrency}
            isDisabled={disabled}
          />
          <SelectEnum<FreightTerm>
            enumValue={enumFreightTerm}
            value={form.freightTerm}
            onChange={handleFieldChange('freightTerm')}
            label={t.quotes.detail.fields.shipmentTerm}
            isDisabled={disabled}
          />
          <div
            className={cn(
              'min-h-input flex w-full items-center self-end rounded-md border border-neutral/20 px-3',
              disabled && 'bg-neutral/5',
            )}
          >
            <Checkbox
              isSelected={form.freightCoveredByCustomer ?? false}
              onChange={handleFieldChange('freightCoveredByCustomer')}
              isDisabled={disabled}
            >
              {t.quotes.detail.fields.freightCoveredByCustomer}
            </Checkbox>
          </div>
        </div>
      </ContentSection>
    </div>
  );
};
